import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Grid,
  LinearProgress,
  MenuItem,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import PaidIcon from "@mui/icons-material/Paid";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useTranslation } from "react-i18next";
import { presentationUserFacingError } from "../../utils/presentationUserFacingError";

const MODE_OPTIONS = [
  {
    id: "presentation",
    Icon: AutoAwesomeIcon,
    color: "secondary",
  },
  {
    id: "trial",
    Icon: RocketLaunchIcon,
    color: "primary",
  },
  {
    id: "pay",
    Icon: PaidIcon,
    color: "success",
  },
];

function normalizeSlug(raw) {
  let s = String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-");
  s = s.replace(/^https?:\/\//, "");
  const slash = s.indexOf("/");
  if (slash >= 0) s = s.slice(0, slash);
  return s.replace(/\.xpro\.me$/i, "").replace(/\.$/, "").trim();
}

const SLUG_RE = /^[a-z0-9][a-z0-9_-]{1,31}$/;

/** ID numérico do domínio após POST /dominios (camelCase ou legado). */
function createdWorkspaceDominioId(res) {
  if (!res || typeof res !== "object") return null;
  const raw = res.dominioId ?? res.dominio_id ?? res.id;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Deriva um identificador a partir da URL do site (apenas para sugestão automática).
 * Ex.: "https://www.piracanjuba.com.br/" → "piracanjuba"
 *      "empresa-x.com"                   → "empresa-x"
 *      "sub.dominio.empresa.com.br"      → "empresa"
 *
 * Estratégia:
 *  1) extrai o hostname (com ou sem protocolo);
 *  2) remove "www.";
 *  3) remove sufixos comuns de TLD/2LD (.com.br, .com, .net, .org, etc.) pegando o "registrable name";
 *  4) sanitiza para o regex de slug: minúsculas, [a-z0-9_-], sem hífen/underline na borda inicial.
 */
function deriveSlugFromSiteUrl(raw) {
  const v = String(raw || "").trim();
  if (!v) return "";
  let host = v;
  host = host.replace(/^https?:\/\//i, "").replace(/^\/\//, "");
  const slash = host.indexOf("/");
  if (slash >= 0) host = host.slice(0, slash);
  host = host.split("?")[0].split("#")[0].split(":")[0];
  host = host.toLowerCase().trim();
  host = host.replace(/^www\./, "");
  if (!host) return "";
  const parts = host.split(".").filter(Boolean);
  if (!parts.length) return "";
  const COMMON_2LD = new Set([
    "com",
    "co",
    "net",
    "org",
    "gov",
    "edu",
    "mil",
    "ac",
    "br",
    "uk",
    "us",
    "io",
    "app",
    "dev",
    "tech",
    "ai",
    "me",
    "tv",
    "xyz",
    "store",
    "online",
    "site",
  ]);
  let candidate = parts[parts.length - 1];
  for (let i = parts.length - 2; i >= 0; i--) {
    if (COMMON_2LD.has(parts[i])) {
      candidate = parts[i - 1] || parts[i];
    } else {
      candidate = parts[i];
      break;
    }
  }
  let slug = String(candidate || "")
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^[-_]+/, "")
    .replace(/[-_]+$/, "");
  if (slug.length > 32) slug = slug.slice(0, 32).replace(/[-_]+$/, "");
  return slug;
}

function PRESENTATION_PIPELINE_STEPS(t) {
  return [
    { key: "queued", label: t("workspaceCreate.progress.queued", { defaultValue: "Na fila…" }) },
    { key: "scraping", label: t("workspaceCreate.progress.scraping", { defaultValue: "Coletando informações do site" }) },
    { key: "generating", label: t("workspaceCreate.progress.generating", { defaultValue: "Gerando dados de demonstração" }) },
    { key: "applying", label: t("workspaceCreate.progress.applying", { defaultValue: "Aplicando no workspace" }) },
    { key: "ready", label: t("workspaceCreate.progress.ready", { defaultValue: "Pronto!" }) },
  ];
}

/** Índice do stepper para status retornado pela API (inclui estados fora da lista linear). */
function presentationStatusStepIndex(status) {
  const s = String(status || "queued").toLowerCase();
  const order = ["queued", "scraping", "generating", "applying", "ready"];
  const i = order.indexOf(s);
  if (i >= 0) return i;
  if (s === "converting") return 3;
  return 0;
}

/**
 * Componente wizard reutilizável para criação de workspaces.
 *
 * Props:
 *  - createWorkspace: async ({ mode, workspace, siteUrl, planSku, planVersion }) => result
 *      result deve incluir { dominioId, presentationId? } na resposta de sucesso.
 *  - getPresentationStatus: async (dominioId) => { presentation: { status, errorMessage, ... } }
 *      usado para polling no modo `presentation`.
 *  - planCatalogItems: array opcional para preencher o seletor de plano.
 *  - allowedModes: array opcional para restringir os modos exibidos (default: todos).
 *  - onCompleted: callback chamado com `{ dominioId, mode, ... }` ao final.
 *  - openWorkspaceUrl: opcional — string ou função (workspaceData) => string para link "abrir".
 */
const WorkspaceCreateWizard = ({
  createWorkspace,
  getPresentationStatus,
  planCatalogItems,
  allowedModes,
  onCompleted,
  openWorkspaceUrl,
  defaultMode,
}) => {
  const { t } = useTranslation("app");
  const modes = useMemo(() => {
    if (!Array.isArray(allowedModes) || !allowedModes.length) return MODE_OPTIONS;
    const set = new Set(allowedModes);
    return MODE_OPTIONS.filter((m) => set.has(m.id));
  }, [allowedModes]);

  const [activeStep, setActiveStep] = useState(0);
  const [mode, setMode] = useState(defaultMode || (modes[0] ? modes[0].id : "trial"));
  const [slug, setSlug] = useState("");
  const [slugCustomized, setSlugCustomized] = useState(false);
  const [siteUrl, setSiteUrl] = useState("");
  const [planSku, setPlanSku] = useState("");
  const [planVersion, setPlanVersion] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [createdResult, setCreatedResult] = useState(null);
  const [presentationStatus, setPresentationStatus] = useState(null);
  const [pollError, setPollError] = useState("");
  const [pollFailureCount, setPollFailureCount] = useState(0);
  const [manualRefreshBusy, setManualRefreshBusy] = useState(false);
  const [staleQueuedHint, setStaleQueuedHint] = useState(false);
  const pollTimerRef = useRef(null);
  const progressStartedAtRef = useRef(null);

  const stepsLabels = useMemo(() => {
    const labels = [
      t("workspaceCreate.steps.mode", { defaultValue: "Tipo de workspace" }),
      mode === "presentation"
        ? t("workspaceCreate.steps.site", { defaultValue: "Dados do site" })
        : t("workspaceCreate.steps.details", { defaultValue: "Dados do workspace" }),
    ];
    if (mode === "presentation") {
      labels.push(t("workspaceCreate.steps.progress", { defaultValue: "Geração da apresentação" }));
    }
    labels.push(t("workspaceCreate.steps.done", { defaultValue: "Concluído" }));
    return labels;
  }, [mode, t]);

  const finalStep = stepsLabels.length - 1;
  const presentationProgressStep = mode === "presentation" ? finalStep - 1 : null;

  const onPresentationProgress =
    presentationProgressStep != null && activeStep === presentationProgressStep;
  const showWizardFooter =
    activeStep !== finalStep && !onPresentationProgress;
  const showBackInFooter = activeStep >= 1 && showWizardFooter;

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const slugError = useMemo(() => {
    if (!slug) return "";
    const norm = normalizeSlug(slug);
    if (!SLUG_RE.test(norm)) {
      return t("workspaceCreate.errors.invalidSlug", {
        defaultValue: "2–32 caracteres: letras minúsculas, números, - ou _",
      });
    }
    return "";
  }, [slug, t]);

  const startPresentationPolling = useCallback(
    (dominioId) => {
      if (!dominioId || !getPresentationStatus) return;
      progressStartedAtRef.current = Date.now();
      setPollError("");
      setPollFailureCount(0);
      setStaleQueuedHint(false);
      const fetchOnce = async () => {
        try {
          const res = await getPresentationStatus(dominioId);
          setPollError("");
          setPollFailureCount(0);
          const polledId = createdWorkspaceDominioId(res);
          if (polledId) {
            setCreatedResult((prev) => {
              if (prev && createdWorkspaceDominioId(prev)) return prev;
              return { ...(prev && typeof prev === "object" ? prev : {}), dominioId: polledId };
            });
          }
          const pres = res?.presentation ?? null;
          setPresentationStatus(pres);
          const status = pres?.status;
          const elapsed = progressStartedAtRef.current ? Date.now() - progressStartedAtRef.current : 0;
          const low = String(status || "").toLowerCase();
          const serverErr =
            (typeof pres?.errorMessage === "string" && pres.errorMessage.trim()) ||
            (typeof pres?.error_message === "string" && pres.error_message.trim()) ||
            "";
          if (low === "failed" || serverErr) {
            setStaleQueuedHint(false);
          } else if (low === "queued" && elapsed > 120_000) {
            setStaleQueuedHint(true);
          } else {
            setStaleQueuedHint(false);
          }
          if (status === "ready" || status === "failed" || status === "discarded") {
            stopPolling();
            if (status === "ready") {
              setActiveStep(finalStep);
            }
            return;
          }
        } catch (e) {
          const msg =
            (typeof e?.response?.data?.message === "string" && e.response.data.message) ||
            e?.message ||
            "";
          setPollError(
            msg ||
              t("workspaceCreate.progress.pollErrorGeneric", {
                defaultValue:
                  "Não foi possível atualizar o status agora. Verifique sua internet, atualize a página ou entre na conta novamente. Se repetir, espere alguns minutos ou entre em contato com o suporte.",
              })
          );
          setPollFailureCount((c) => c + 1);
        }
        pollTimerRef.current = setTimeout(fetchOnce, 4000);
      };
      stopPolling();
      pollTimerRef.current = setTimeout(fetchOnce, 1500);
    },
    [getPresentationStatus, stopPolling, finalStep, t]
  );

  const refreshPresentationStatus = useCallback(async () => {
    const id = createdWorkspaceDominioId(createdResult);
    if (!id || !getPresentationStatus) return;
    setManualRefreshBusy(true);
    setPollError("");
    try {
      const res = await getPresentationStatus(id);
      setPollFailureCount(0);
      const pres = res?.presentation ?? null;
      setPresentationStatus(pres);
      const status = pres?.status;
      if (status === "ready" || status === "failed" || status === "discarded") {
        stopPolling();
        if (status === "ready") setActiveStep(finalStep);
      }
    } catch (e) {
      const msg =
        (typeof e?.response?.data?.message === "string" && e.response.data.message) ||
        e?.message ||
        t("workspaceCreate.progress.pollErrorGeneric", {
          defaultValue:
            "Não foi possível atualizar o status agora. Verifique sua internet, atualize a página ou entre na conta novamente. Se repetir, espere alguns minutos ou entre em contato com o suporte.",
        });
      setPollError(msg);
    } finally {
      setManualRefreshBusy(false);
    }
  }, [createdResult, getPresentationStatus, stopPolling, finalStep, t]);

  const handleSubmit = useCallback(async () => {
    setError("");
    const norm = normalizeSlug(slug);
    if (!SLUG_RE.test(norm)) {
      setError(
        t("workspaceCreate.errors.invalidSlug", {
          defaultValue: "Informe um identificador válido (2–32 caracteres).",
        })
      );
      return;
    }
    if (mode === "presentation") {
      if (!siteUrl.trim()) {
        setError(t("workspaceCreate.errors.siteRequired", { defaultValue: "Informe o site da empresa." }));
        return;
      }
    }
    if (mode === "pay" && !planSku) {
      setError(t("workspaceCreate.errors.planRequired", { defaultValue: "Selecione um plano." }));
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        mode,
        workspace: norm,
      };
      if (mode === "presentation") {
        payload.siteUrl = siteUrl.trim();
      }
      if ((mode === "trial" || mode === "pay") && planSku) {
        payload.planSku = planSku;
        payload.planVersion = Number(planVersion) || 1;
      }
      const res = await createWorkspace(payload);
      const dominioId = createdWorkspaceDominioId(res);
      const resNorm = res && typeof res === "object" ? { ...res, dominioId } : res;
      setCreatedResult(resNorm);
      if (mode === "presentation") {
        if (!dominioId) {
          setError(
            t("workspaceCreate.errors.missingDominioId", {
              defaultValue:
                "Não foi possível confirmar a criação do workspace. Atualize a página e tente novamente, ou fale com o suporte.",
            })
          );
          return;
        }
        setActiveStep(presentationProgressStep);
        startPresentationPolling(dominioId);
      } else {
        setActiveStep(finalStep);
      }
      if (typeof onCompleted === "function" && mode !== "presentation") {
        onCompleted({ ...(resNorm || {}), mode });
      }
    } catch (e) {
      const msg =
        (typeof e?.response?.data?.message === "string" && e.response.data.message) ||
        e?.message ||
        t("workspaceCreate.errors.submitFailed", {
          defaultValue:
            "Não foi possível criar o workspace agora. Confira os dados e tente de novo. Se o problema continuar, entre em contato com o suporte.",
        });
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }, [
    slug,
    mode,
    siteUrl,
    planSku,
    planVersion,
    createWorkspace,
    presentationProgressStep,
    finalStep,
    onCompleted,
    startPresentationPolling,
    t,
  ]);

  const handleNext = () => {
    if (activeStep === 1) {
      handleSubmit();
      return;
    }
    setActiveStep((s) => Math.min(finalStep, s + 1));
  };

  const handleBack = () => {
    setActiveStep((s) => Math.max(0, s - 1));
  };

  const renderModeStep = () => (
    <Grid container spacing={2}>
      {modes.map((opt) => {
        const Icon = opt.Icon;
        const selected = mode === opt.id;
        return (
          <Grid item xs={12} md={4} key={opt.id}>
            <Card
              variant={selected ? "elevation" : "outlined"}
              elevation={selected ? 3 : 0}
              sx={(theme) => {
                const c = theme.palette[opt.color];
                return {
                  height: "100%",
                  borderColor: selected ? c.main : theme.palette.divider,
                  borderWidth: selected ? 2 : 1,
                  bgcolor: selected ? alpha(c.main, 0.08) : theme.palette.background.paper,
                  transition: theme.transitions.create(["box-shadow", "border-color", "background-color"], {
                    duration: theme.transitions.duration.shorter,
                  }),
                  "&:hover": {
                    borderColor: selected ? c.main : alpha(theme.palette.text.primary, 0.28),
                  },
                };
              }}
            >
              <CardActionArea onClick={() => setMode(opt.id)} sx={{ height: "100%", alignItems: "stretch" }}>
                <CardContent>
                  <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ mb: 1.25 }}>
                    <Avatar
                      variant="rounded"
                      sx={(theme) => ({
                        bgcolor: alpha(theme.palette[opt.color].main, selected ? 0.22 : 0.12),
                        width: 44,
                        height: 44,
                      })}
                    >
                      <Icon fontSize="small" color={opt.color} />
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" component="h3" fontWeight={700}>
                        {t(`workspaceCreate.mode.${opt.id}.title`, {
                          defaultValue:
                            opt.id === "presentation"
                              ? "Apresentação"
                              : opt.id === "trial"
                                ? "Trial"
                                : "Pagamento",
                        })}
                      </Typography>
                      {selected ? (
                        <Typography variant="caption" color="primary" fontWeight={600} sx={{ display: "block", mt: 0.25 }}>
                          {t("workspaceCreate.modeSelectedPill", { defaultValue: "Selecionado" })}
                        </Typography>
                      ) : null}
                    </Box>
                    {selected ? <CheckCircleIcon color={opt.color} sx={{ fontSize: 22, mt: 0.25 }} /> : null}
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                    {t(`workspaceCreate.mode.${opt.id}.description`, {
                      defaultValue:
                        opt.id === "presentation"
                          ? "Workspace demo populado por IA com base no site da empresa."
                          : opt.id === "trial"
                            ? "Workspace real com período de avaliação gratuito."
                            : "Workspace real com cobrança imediata.",
                    })}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );

  const renderDetailsStep = () => {
    const slugField = (
      <TextField
        label={t("workspaceCreate.fields.slug", { defaultValue: "Identificador (subdomínio)" })}
        helperText={
          slugError ||
          t("workspaceCreate.fields.slugHelp", {
            defaultValue: "Apenas letras minúsculas, números, - ou _ (2 a 32 caracteres).",
          })
        }
        error={Boolean(slugError)}
        value={slug}
        onChange={(e) => {
          setSlug(e.target.value);
          setSlugCustomized(true);
        }}
        size="small"
        autoFocus={mode !== "presentation"}
        fullWidth
      />
    );

    return (
      <Stack spacing={2}>
        {mode === "presentation" ? (
          <>
            <TextField
              label={t("workspaceCreate.fields.siteUrl", { defaultValue: "Site da empresa" })}
              helperText={t("workspaceCreate.fields.siteUrlHelp", {
                defaultValue:
                  "Coletamos automaticamente as informações públicas do site para popular a demo.",
              })}
              value={siteUrl}
              onChange={(e) => {
                const value = e.target.value;
                setSiteUrl(value);
                if (!slugCustomized) {
                  const auto = deriveSlugFromSiteUrl(value);
                  setSlug(auto);
                }
              }}
              placeholder="https://www.empresa.com.br"
              size="small"
              autoFocus
              fullWidth
            />
            {slugField}
            <Alert severity="info" variant="outlined">
              {t("workspaceCreate.presentationDisclaimer", {
                defaultValue:
                  "Este workspace é exclusivamente para demonstração. Todos os clientes, leads, conversas e métricas serão fictícios, gerados por IA com base no site informado.",
              })}
            </Alert>
          </>
        ) : (
          slugField
        )}

        {mode === "trial" || mode === "pay" ? (
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={8}>
              <TextField
                select
                label={t("workspaceCreate.fields.plan", { defaultValue: "Plano" })}
                value={planSku}
                onChange={(e) => setPlanSku(e.target.value)}
                size="small"
                fullWidth
                helperText={
                  mode === "pay"
                    ? t("workspaceCreate.fields.planRequiredForPay", {
                        defaultValue: "Obrigatório para o modo Pagamento.",
                      })
                    : t("workspaceCreate.fields.planOptional", {
                        defaultValue: "Opcional. Mantém o plano de trial padrão se não informado.",
                      })
                }
              >
                <MenuItem value="">
                  <em>{t("workspaceCreate.fields.planNone", { defaultValue: "Padrão" })}</em>
                </MenuItem>
                {(planCatalogItems || []).map((p) => (
                  <MenuItem key={`${p.sku}-${p.version}`} value={p.sku}>
                    {p.name || p.sku} — v{p.version}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                type="number"
                label={t("workspaceCreate.fields.planVersion", { defaultValue: "Versão" })}
                value={planVersion}
                onChange={(e) => setPlanVersion(e.target.value)}
                size="small"
                fullWidth
                helperText={t("workspaceCreate.fields.planVersionHelp", {
                  defaultValue: "Número da versão do SKU no catálogo (em geral 1).",
                })}
                inputProps={{ min: 1 }}
              />
            </Grid>
          </Grid>
        ) : null}
      </Stack>
    );
  };

  const renderProgressStep = () => {
    const pipeline = PRESENTATION_PIPELINE_STEPS(t);
    const currentStatus = presentationStatus?.status || "queued";
    const rawErrorMsg =
      presentationStatus?.errorMessage ||
      presentationStatus?.error_message ||
      "";
    const errorDisplay = presentationUserFacingError(rawErrorMsg, t);
    const errorWasSanitized =
      Boolean(String(rawErrorMsg).trim()) &&
      String(errorDisplay).trim() !== String(rawErrorMsg).trim();
    const failed = String(currentStatus || "").toLowerCase() === "failed";
    const safeIndex = presentationStatusStepIndex(currentStatus);
    const dominioId = createdWorkspaceDominioId(createdResult);
    const updatedAt = presentationStatus?.updatedAt;
    const showStaleQueuedFallback =
      staleQueuedHint && !failed && !pollError;

    return (
      <Stack spacing={2}>
        {!failed ? (
          <Typography variant="body2" color="text.secondary">
            {t("workspaceCreate.progress.intro", {
              defaultValue:
                "Estamos coletando informações do site, gerando dados de demonstração e aplicando no workspace. Isso costuma levar de 1 a 3 minutos.",
            })}
          </Typography>
        ) : null}

        {failed ? (
          <Alert severity="error">
            {t("workspaceCreate.progress.failed", {
              defaultValue: "Não foi possível concluir a demonstração automática.",
            })}
            {errorDisplay ? (
              errorWasSanitized ? (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {errorDisplay}
                </Typography>
              ) : (
                <Box sx={{ mt: 1, fontFamily: "monospace", fontSize: 12, whiteSpace: "pre-wrap" }}>
                  {errorDisplay}
                </Box>
              )
            ) : (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {t("workspaceCreate.progress.failedNoDetail", {
                  defaultValue:
                    "O sistema não informou o motivo exato. Você pode descartar este workspace e criar outro, ou abrir os detalhes para tentar de novo. Se precisar de ajuda, fale com o suporte e informe o número do workspace.",
                })}
              </Typography>
            )}
          </Alert>
        ) : null}

        {pollError ? (
          <Alert
            severity={pollFailureCount >= 1 ? "error" : "warning"}
            onClose={() => setPollError("")}
          >
            {pollError}
            {pollFailureCount >= 2
              ? ` (${t("workspaceCreate.progress.pollFailures", { count: pollFailureCount, defaultValue: "{{count}} tentativas falhas" })})`
              : null}
          </Alert>
        ) : null}

        {showStaleQueuedFallback ? (
          <Alert severity="warning">
            {t("workspaceCreate.progress.staleQueued", {
              defaultValue:
                "A preparação está na fila há mais tempo que o habitual — às vezes o serviço está mais lento. Toque em Atualizar status ou aguarde alguns minutos. Se não mudar, abra os detalhes deste workspace ou fale com o suporte informando o número acima.",
            })}
          </Alert>
        ) : null}

        {!failed ? (
          <Box>
            {!presentationStatus || currentStatus !== "ready" ? (
              <LinearProgress
                sx={(theme) => ({
                  height: 8,
                  borderRadius: 1,
                  mb: 2.5,
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  "& .MuiLinearProgress-bar": { borderRadius: 1 },
                })}
              />
            ) : null}
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              {t("workspaceCreate.progress.pipelineCaption", {
                defaultValue: "Etapas da preparação (atualizamos automaticamente a cada poucos segundos):",
              })}
            </Typography>
            <Stepper activeStep={safeIndex} alternativeLabel sx={{ py: 0.5 }}>
              {pipeline.map((s) => (
                <Step key={s.key}>
                  <StepLabel
                    StepIconProps={{
                      sx: { "&.Mui-active": { fontWeight: 700 }, "&.Mui-completed": { color: "success.main" } },
                    }}
                  >
                    {s.label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        ) : null}

        <Stack spacing={0.75}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Button
              type="button"
              size="small"
              variant="outlined"
              disabled={manualRefreshBusy || !dominioId}
              onClick={refreshPresentationStatus}
            >
              {manualRefreshBusy
                ? t("workspaceCreate.progress.refreshing", { defaultValue: "Atualizando…" })
                : t("workspaceCreate.progress.refreshStatus", { defaultValue: "Atualizar status" })}
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 560 }}>
            {t("workspaceCreate.progress.refreshManualHint", {
              defaultValue: "Use só se a etapa parecer travada por vários minutos. O fluxo segue sozinho na maioria dos casos.",
            })}
          </Typography>
        </Stack>

        {dominioId || updatedAt ? (
          <Typography variant="caption" color="text.secondary" component="div" sx={{ lineHeight: 1.5 }}>
            {dominioId
              ? t("workspaceCreate.progress.dominioId", { id: dominioId, defaultValue: "ID do workspace: {{id}}" })
              : null}
            {dominioId && updatedAt ? " · " : null}
            {updatedAt
              ? t("workspaceCreate.progress.lastServerUpdate", {
                  defaultValue: "Última atualização no servidor: {{time}}",
                  time: String(updatedAt),
                })
              : null}
          </Typography>
        ) : null}

        {!failed ? (
          <Alert severity="info" variant="outlined">
            {t("workspaceCreate.progress.runningInBackground", {
              defaultValue:
                "Você pode fechar esta janela — a preparação continua em segundo plano. Quando estiver pronta, o status será atualizado aqui ou na lista de workspaces.",
            })}
          </Alert>
        ) : null}
      </Stack>
    );
  };

  const renderDoneStep = () => {
    const dominioId = createdWorkspaceDominioId(createdResult);
    const createdForLink =
      createdResult && typeof createdResult === "object"
        ? { ...createdResult, dominioId: dominioId ?? createdResult.dominioId }
        : createdResult;
    let openUrl = null;
    if (typeof openWorkspaceUrl === "function") openUrl = openWorkspaceUrl(createdForLink);
    else if (typeof openWorkspaceUrl === "string") openUrl = openWorkspaceUrl;

    return (
      <Stack spacing={2} alignItems="center" sx={{ py: 2 }}>
        <CheckCircleIcon color="success" sx={{ fontSize: 64 }} />
        <Typography variant="h6">
          {t("workspaceCreate.done.title", { defaultValue: "Workspace criado!" })}
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          {mode === "presentation"
            ? t("workspaceCreate.done.subtitlePresentation", {
                defaultValue: "O workspace de apresentação está pronto para ser exibido.",
              })
            : t("workspaceCreate.done.subtitleProduction", {
                defaultValue: "Tudo certo! Você já pode acessar o workspace.",
              })}
        </Typography>
        {dominioId ? (
          <Typography variant="caption" color="text.secondary">
            {t("workspaceCreate.done.dominioId", { defaultValue: "ID #{{id}}", id: dominioId })}
          </Typography>
        ) : null}
        {openUrl ? (
          <Button variant="contained" href={openUrl}>
            {t("workspaceCreate.done.open", { defaultValue: "Abrir workspace" })}
          </Button>
        ) : null}
      </Stack>
    );
  };

  return (
    <Box sx={{ minWidth: 0 }}>
      {onPresentationProgress ? (
        <Box
          sx={(theme) => ({
            mb: 3,
            pb: 2.5,
            borderBottom: `1px solid ${theme.palette.divider}`,
          })}
        >
          <Typography variant="overline" color="primary" sx={{ fontWeight: 700, letterSpacing: 0.8 }}>
            {t("workspaceCreate.progress.phaseKicker", { defaultValue: "Demonstração automática" })}
          </Typography>
          <Typography variant="h6" component="p" sx={{ mt: 0.5, fontWeight: 700 }}>
            {t("workspaceCreate.progress.phaseTitle", {
              defaultValue: "Estamos montando o workspace de apresentação",
            })}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 720, lineHeight: 1.55 }}>
            {t("workspaceCreate.progress.phaseSub", {
              defaultValue:
                "A barra e as etapas abaixo mostram o andamento. Não é necessário atualizar a página — o status muda sozinho.",
            })}
          </Typography>
        </Box>
      ) : (
        <Stepper
          activeStep={activeStep}
          sx={(theme) => ({
            mb: 3,
            "& .MuiStepConnector-line": { borderTopWidth: 3, borderColor: alpha(theme.palette.text.primary, 0.12) },
            "& .MuiStepLabel-label": { typography: { xs: "caption", sm: "body2" }, fontWeight: 500 },
            "& .MuiStepLabel-label.Mui-active": { fontWeight: 700 },
          })}
        >
          {stepsLabels.map((label, idx) => (
            <Step key={`${label}-${idx}`}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      )}

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}

      {activeStep === 0 ? renderModeStep() : null}
      {activeStep === 1 ? renderDetailsStep() : null}
      {presentationProgressStep != null && activeStep === presentationProgressStep
        ? renderProgressStep()
        : null}
      {activeStep === finalStep ? renderDoneStep() : null}

      {showWizardFooter ? (
        <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" sx={{ mt: 4, pt: 2, borderTop: 1, borderColor: "divider" }}>
          {showBackInFooter ? (
            <Button variant="outlined" onClick={handleBack} disabled={submitting}>
              {t("workspaceCreate.actions.back", { defaultValue: "Voltar" })}
            </Button>
          ) : (
            <span />
          )}
          {activeStep < 2 ? (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={submitting || (activeStep === 1 && Boolean(slugError))}
              sx={
                submitting
                  ? (theme) => ({
                      "&.Mui-disabled": {
                        bgcolor: alpha(theme.palette.primary.main, 0.85),
                        color: theme.palette.primary.contrastText,
                      },
                    })
                  : undefined
              }
            >
              {submitting ? (
                <Stack direction="row" alignItems="center" spacing={1.25} component="span">
                  <CircularProgress size={18} color="inherit" thickness={5} />
                  <span>{t("workspaceCreate.actions.submitting", { defaultValue: "Criando…" })}</span>
                </Stack>
              ) : activeStep === 1 ? (
                t("workspaceCreate.actions.create", { defaultValue: "Criar workspace" })
              ) : (
                t("workspaceCreate.actions.next", { defaultValue: "Avançar" })
              )}
            </Button>
          ) : null}
        </Stack>
      ) : null}
    </Box>
  );
};

export default WorkspaceCreateWizard;
